"use client";

import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { useTranslations } from "@/i18n/client";
import { countries, useGeo } from "@/i18n/routing";
import type { GeoLocale } from "@frend-digital/next/i18n";
import { useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { fetchRawCentraCountry } from "./fetchers";
import styles from "./GeoPopup.module.css";
import { useGeoPopup } from "./hooks";

export interface GeoPopupProps {
	selectedCountry: string;
}

const UnsuspendedGeoPopup = ({ selectedCountry }: GeoPopupProps) => {
	const t = useTranslations("geo");
	const { action, language } = useGeo();
	const { data, isLoading, error } = useQuery({
		queryKey: ["geo"],
		queryFn: fetchRawCentraCountry,
	});

	const { isVisible, dismiss } = useGeoPopup({
		selectedCountry,
		centraCountry: data?.centraCountry ?? "",
	});

	if (isLoading || error || !data?.centraCountry || !selectedCountry || !isVisible) {
		return null;
	}

	const countriesMatch = selectedCountry === data.centraCountry;

	if (countriesMatch || !countries.find((c) => c.country === data.centraCountry)) {
		return null;
	}

	const updateCountry = () => {
		const geo = `${language}-${data.centraCountry}` as GeoLocale;
		action(geo);
	};

	return (
		<aside className={styles.wrapper}>
			<div className={styles.root}>
				<Text variant="body2">
					{t("popup.shoppingFrom", { country: t(`country.${data.centraCountry}`) })}
				</Text>
				<Text variant="body3">
					{t("popup.text", { country: t(`country.${data.centraCountry}`) })}
				</Text>
				<div className={styles.actions}>
					<Button className={styles.button} color="darkblue" onClick={updateCountry}>
						{t("popup.yes", { country: t(`country.${data.centraCountry}`) })}
					</Button>
					<Button className={styles.button} color="white" onClick={dismiss}>
						{t("popup.no", { country: t(`country.${selectedCountry}`) })}
					</Button>
				</div>
			</div>
		</aside>
	);
};

export const GeoPopup: typeof UnsuspendedGeoPopup = (props) => {
	return (
		<Suspense>
			<UnsuspendedGeoPopup {...props} />
		</Suspense>
	);
};
